import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, useToast } from "@swordsweeper/ssi-ui-component-library";
import Tile from "@src/components/containers/Tile";
import ProductImage from "@src/components/widgets/ProductImage";
import DistributorLogo from "@src/components/common/DistributorLogo";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import * as CartApi from "@src/api/CartApi";
import * as SavedDistributorProductApi from "@src/api/SavedDistributorProductApi";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import { useCartStore } from "@src/state/cartState";
import { useSavedForLaterStore } from "@src/state/savedForLaterState";
import { SavedDistributorProduct } from "@src/types/savedDistributorProduct";
import styles from "./SavedForLaterProductTile.module.scss";
import classnames from "classnames";
import Modal, {
  ModalSizeOptions,
  ModalSizes,
} from "@src/components/common/Modal";
import ProductOtherOptions from "../ProductOtherOptions";

interface SavedForLaterProductTileProps {
  savedForLater: SavedDistributorProduct;
}

export default function SavedForLaterProductTile({
  savedForLater,
}: SavedForLaterProductTileProps): React.JSX.Element {
  const { showToast } = useToast();
  const { refreshCart, refreshCartCount } = useCartStore();
  const { savedForLaters, refreshSavedForLater, setSavedForLater } =
    useSavedForLaterStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showOtherOptionsModal, setShowOtherOptionsModal] = useState(false);

  const distributorProduct = savedForLater.distributorProduct;
  const product = distributorProduct?.product;
  const navigate = useNavigate();

  const imageSrc =
    distributorProduct?.images?.[0]?.imageUrl ||
    product?.thumbnailUrl ||
    product?.imageUrl;

  const price = distributorProduct?.livePricing?.price;
  const isConnected = distributorProduct?.livePricing?.isConnected === true;
  const hasPrice = isConnected && typeof price === "number" && price > 0;
  const distributorSlug = distributorProduct?.distributor?.slug || "";
  const isInStock = distributorProduct?.livePricing?.isInStock || false;
  const buttonDisabled =
    !distributorProduct?.livePricing ||
    !isConnected ||
    Boolean(distributorProduct?.livePricing?.cartButtonDisabled) ||
    !isInStock;
  const displayItemName = (() => {
    const itemName = product?.itemName || "Product";
    const unitOfMeasure = product?.unitOfMeasure?.trim();
    const packageQuantity = product?.packageQuantity;
    if (!unitOfMeasure) return itemName;
    const unitHasLettersAndNumbers = /[A-Za-z].*\d|\d.*[A-Za-z]/.test(
      unitOfMeasure,
    );
    const suffix =
      !unitHasLettersAndNumbers && packageQuantity ? ` ${packageQuantity}` : "";
    return `${itemName}, ${unitOfMeasure}${suffix}`;
  })();
  const optimisticallyRemoveSaved = useCallback(() => {
    if (!savedForLaters?.length) return null;
    const nextSaved = savedForLaters.filter(
      (item) => `${item.id}` !== `${savedForLater.id}`,
    );
    setSavedForLater(nextSaved);
    return savedForLaters;
  }, [savedForLaters, savedForLater.id, setSavedForLater]);

  const handleRemoveSaved = async () => {
    if (isLoading) return;
    setIsLoading(true);
    const previousSaved = optimisticallyRemoveSaved();
    try {
      await SavedDistributorProductApi.deleteSavedDistributorProduct(
        savedForLater.id,
      );
      await refreshSavedForLater();
      showToast({
        message: "Removed from saved items",
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      if (previousSaved) {
        setSavedForLater(previousSaved);
      }
      showToast({
        message: "Error removing saved item",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoveToCart = async () => {
    if (!distributorProduct?.id) {
      showToast({
        message: "Unable to move this item to cart.",
        variant: "error",
      });
      return;
    }

    if (isLoading) return;
    setIsLoading(true);
    try {
      await CartApi.addToCart(String(distributorProduct.id), 1);
      await refreshCart();
      const previousSaved = optimisticallyRemoveSaved();
      await SavedDistributorProductApi.deleteSavedDistributorProduct(
        savedForLater.id,
      );
      await refreshSavedForLater();
      showToast({
        message: "Moved to cart",
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      // If the optimistic saved removal ran, refresh to resync.
      await refreshSavedForLater();
      showToast({
        message: "Error moving item to cart",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleProductClick = () => {
    if (product?.id) {
      navigate(`/product/${product?.id}/`);
    }
  };
  return (
    <Tile
      className={styles.card}
      data-testid={`saved-for-later-product-tile-${savedForLater.id}`}
    >
      {isLoading && <SpinningLoader whiteBackground />}
      <div className={styles.content}>
        {product?.id ? (
          <img
            className={classnames(styles.productImage, {
              clickable: !!product?.id,
            })}
            src={imageSrc}
            alt={product?.itemName}
            onClick={handleProductClick}
          />
        ) : (
          <img
            className={classnames(styles.productImage, {
              clickable: !!product?.id,
            })}
            src={imageSrc}
            alt={product?.itemName}
            onClick={handleProductClick}
          />
        )}
        <div className={styles.details}>
          <div>
            <div
              className={classnames(styles.titleWrap, {
                clickable: !!product?.id,
              })}
              onClick={handleProductClick}
              id={`saved-for-later-tile-item-name-${savedForLater.id}`}
            >
              {displayItemName}
            </div>
          </div>
          <div className={styles.priceRow}>
            {hasPrice ? (
              <PriceDisplay
                amount={price}
                className={styles.price}
                data-testid={`saved-for-later-product-tile-price-${savedForLater.id}`}
              />
            ) : (
              <div className={styles.priceUnavailable}>Pricing Unavailable</div>
            )}
            {!!distributorSlug && (
              <DistributorLogo distributorSlug={distributorSlug} />
            )}
          </div>

          <div className={styles.actions}>
            <Button
              id={`saved-for-later-tile-move-to-cart-button-${savedForLater.id}`}
              data-testid={`saved-for-later-tile-move-to-cart-button-${savedForLater.id}`}
              className={classnames(
                "btn btn-primary btn-bordered btn-sm margin-bottom-xs",
                styles.moveToCartButton,
              )}
              disabled={buttonDisabled || isLoading}
              onClick={handleMoveToCart}
            >
              Move to Cart
            </Button>
            <Button
              id={`saved-for-later-tile-shop-other-options-button-${savedForLater.id}`}
              data-testid={`saved-for-later-tile-shop-other-options-button-${savedForLater.id}`}
              className={styles.secondaryLink}
              onClick={() => setShowOtherOptionsModal(true)}
            >
              Shop Other Options <i className="fa-light fa-chevron-down" />
            </Button>

            <Button
              id={`saved-for-later-tile-remove-button-${savedForLater.id}`}
              data-testid={`saved-for-later-tile-remove-button-${savedForLater.id}`}
              className={styles.secondaryLink}
              onClick={handleRemoveSaved}
              disabled={isLoading}
            >
              Remove from Cart
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isShowing={showOtherOptionsModal}
        onClose={() => setShowOtherOptionsModal(false)}
        header={`Other Options for ${product?.itemName}`}
        size={ModalSizes.Large}
        closeOnBackdropClick={false}
      >
        {showOtherOptionsModal && product?.id && (
          <ProductOtherOptions
            productId={String(product.id)}
            productImageSrc={imageSrc}
          />
        )}
      </Modal>
    </Tile>
  );
}
